/**
 * Shared form validation and UX utilities
 */

class FormValidator {
  constructor(formElement) {
    this.form = formElement;
    this.fields = Array.from(
      this.form.querySelectorAll(".form-input, .form-textarea")
    );
    this.hasServerErrors =
      this.form.hasAttribute("data-has-server-errors") &&
      this.form.getAttribute("data-has-server-errors") !== "false";
    this.form.setAttribute("novalidate", "novalidate");

    if (!this.hasServerErrors) {
      this.fields.forEach((field) => this.clearFieldError(field));
    }

    this.init();
  }

  init() {
    this.setupFieldValidation();
    this.setupFormSubmission();
  }

  setupFieldValidation() {
    this.fields.forEach((field) => {
      // Add event listeners
      field.addEventListener("blur", () => this.validateField(field));
      field.addEventListener("input", () => this.clearFieldError(field));
      field.addEventListener("invalid", (e) => e.preventDefault());

      // Handle autocomplete
      field.addEventListener("animationstart", (e) => {
        if (e.animationName === "onAutoFillStart") {
          this.preserveAutocompleteStyles(field);
        }
      });
    });
  }

  setupFormSubmission() {
    this.form.addEventListener("submit", (e) => {
      const isValid = this.validateForm();
      if (!isValid) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    const submitButtons = Array.from(
      this.form.querySelectorAll(
        'button[type="submit"], input[type="submit"], button:not([type])'
      )
    );

    submitButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        const isValid = this.validateForm();
        if (!isValid) {
          event.preventDefault();
          event.stopPropagation();
        }
      });
    });
  }

  validateField(field) {
    const value = field.value.trim();
    const wrapper = field.closest(".form-field-wrapper");
    const fieldName = this.getFieldName(field);

    // Clear existing error
    this.clearFieldError(field);

    let isValid = true;
    let errorMessage = "";

    // Required field validation
    if (field.hasAttribute("required") && !value) {
      isValid = false;
      errorMessage = `${fieldName} is required`;
    }
    // Email validation
    else if (field.type === "email" && value && !this.isValidEmail(value)) {
      isValid = false;
      errorMessage = "Please enter a valid email address";
    }
    // Title validation (min 3 chars)
    else if (fieldName === "title" && value && value.length < 3) {
      isValid = false;
      errorMessage = "Title must be at least 3 characters";
    }
    // Comment validation (min 10 chars)
    else if (fieldName === "comment" && value && value.length < 10) {
      isValid = false;
      errorMessage = "Comment must be at least 10 characters";
    }

    if (!isValid) {
      this.showFieldError(field, errorMessage);
    }

    return isValid;
  }

  validateForm() {
    let isFormValid = true;
    let firstInvalidField = null;

    this.fields.forEach((field) => {
      const fieldIsValid = this.validateField(field);

      if (!fieldIsValid) {
        if (!firstInvalidField) {
          firstInvalidField = field;
        }
        isFormValid = false;
      }
    });

    if (!isFormValid && firstInvalidField) {
      firstInvalidField.focus();
    }

    return isFormValid;
  }

  showFieldError(field, message) {
    const wrapper = field.closest(".form-field-wrapper");
    const fieldContainer = field.closest(".form-field");
    const fieldName = this.getFieldName(field);

    // Add error class
    wrapper.classList.add("error");

    // Reuse existing error element if present (e.g., server-rendered)
    let errorEl = fieldContainer.querySelector(".form-error");
    if (!errorEl) {
      errorEl = document.createElement("div");
      errorEl.className = "form-error";
      fieldContainer.appendChild(errorEl);
    }

    errorEl.setAttribute("data-field", fieldName);
    errorEl.setAttribute("role", "alert");
    errorEl.textContent = message;
  }

  clearFieldError(field) {
    const wrapper = field.closest(".form-field-wrapper");
    const fieldContainer = field.closest(".form-field");

    // Remove error class
    wrapper.classList.remove("error");

    // Remove any error messages inside the field container
    fieldContainer
      .querySelectorAll(".form-error")
      .forEach((errorNode) => errorNode.remove());
  }

  getFieldName(field) {
    const label = field.closest(".form-field").querySelector(".form-label");
    return label ? label.textContent.replace("*", "").trim() : field.name;
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  preserveAutocompleteStyles(field) {
    field.style.setProperty(
      "-webkit-box-shadow",
      "0 0 0 30px var(--real-black-50) inset",
      "important"
    );
    field.style.setProperty(
      "-webkit-text-fill-color",
      "var(--white)",
      "important"
    );
    field.style.setProperty("background-color", "transparent", "important");
  }
}

// Initialize forms when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  const forms = document.querySelectorAll(
    "form[data-form-validator], form.js-form-validator"
  );
  forms.forEach((form) => {
    new FormValidator(form);
  });
});

// Export for use in other scripts
window.FormValidator = FormValidator;
